import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
	PunchupStackedBarChart,
	CHART_SERIES_COLORS,
} from 'features/common/components/Punchup/primitives/charts/PunchupStackedBarChart';
import { PunchupDonutChart, DonutSlice } from 'features/common/components/Punchup/primitives/charts/PunchupDonutChart';
import { PunchupAnalyticsSection } from 'features/common/components/Punchup/primitives/charts/PunchupAnalyticsSection';
import { PunchupAnalyticsEmptyState } from 'features/common/components/Punchup/primitives/charts/PunchupAnalyticsEmptyState';
import { PunchupHelpButton } from 'features/common/components/Punchup/composites/content/PunchupHelpButton';
import { useGetScanHistory } from 'hooks/useGetScanHistory';
import { useEventCapacity } from 'features/common/hooks/useEventCapacity';
import { formatCompactNumber } from 'features/common/utils/chartFormatters';
import { lightenHex } from 'features/common/utils/chartColors';
import { common } from 'theme';
import { bucketArrivals } from './arrivalsBuckets';
import { splitAttendance } from './attendanceBreakdown';
import { PunchupAttendanceByType } from './PunchupAttendanceByType';
import styles from './PunchupVenueOperationsSection.module.css';

const DONUT_SIZE = 180;
// "Yet to arrive" pairs with "Arrived" as the SAME hue, lighter — so the two read as one dimension (in vs not
// in), not two competing colours. The over-capacity band pairs the red the same way. 0.5 = halfway to white.
const AWAITING_FADE = 0.5;
const ARRIVED_COLOR = CHART_SERIES_COLORS[0]; // intense — within-hall arrivals
const AWAITING_COLOR = lightenHex(ARRIVED_COLOR, AWAITING_FADE); // faded — within-hall, not in yet
const OVER_ARRIVED_COLOR = common.colors.red; // intense — over-capacity arrivals
const OVER_AWAITING_COLOR = lightenHex(common.colors.red, AWAITING_FADE); // faded — over-capacity, not in yet

const PunchupVenueOperationsSection: FC = () => {
	const { t } = useTranslation();
	const { data: scanHistory, isLoading: historyLoading } = useGetScanHistory();
	// Hall capacity — the SAME pool-aware denominator as the Fill Rate KPI, so the attendance donut's
	// ring (capacity) and that tile agree. Capacity-only path (no compedByType) is supported by the hook.
	// `types` is forwarded to the per-type attendance row so it does NOT subscribe its own capacity/scan
	// observers (one read per query for the whole section — avoids the duplicate-observer refetch churn).
	const { totals: capacityTotals, types: capacityTypes, isLoading: capacityLoading } = useEventCapacity();

	const isLoading = historyLoading || capacityLoading;

	// Arrivals over time — per-ticket scan timestamps are the only temporal scan data the API exposes
	// (the /scans endpoint keeps just MAX(scanned_at) per scanner, with no per-scan-event log — so a
	// per-gate arrivals timeline is not derivable; gate detail lives on the Scanned Tickets page).
	const arrivals = useMemo(
		() => bucketArrivals((scanHistory?.tickets ?? []).map((entry) => entry.ticket.scannedAt)),
		[scanHistory]
	);
	const totalArrivals = arrivals.reduce((sum, b) => sum + b.count, 0);

	// Attendance against the hall (aligns with the Fill Rate KPI up top). `issued` is the shared single source
	// (capacityTotals.issued = Σ per-type resolved issued), NOT scanHistory.totalSold — the two disagree by the
	// backend two-writer delta, which previously made "yet to arrive" off by one. scanHistory still owns
	// `scanned` (the door count) and the arrivals timeline. The split math (incl. the over-capacity band)
	// lives in splitAttendance; arrivals fill the within-hall band before the over-issue band.
	const capacity = capacityTotals.effectiveCapacity;
	const scanned = scanHistory?.totalScanned ?? 0;
	const { arrived, yetToArrive, arrivedOver, overCapacity, emptySeats } = splitAttendance(
		capacityTotals.issued,
		capacity,
		scanned
	);

	// Order: the within-hall pair (intense → faded), then the over-capacity pair (intense → faded), then the
	// neutral empty remainder last. Same-hue intensity pairs read as "in vs not in", not competing colours.
	// Memoised so the slice array keeps a stable reference for the React.memo'd PunchupDonutChart.
	const attendanceData: DonutSlice[] = useMemo(
		() =>
			[
				arrived > 0 ? { label: t('analytics-venue-arrived'), value: arrived, color: ARRIVED_COLOR } : null,
				yetToArrive > 0
					? { label: t('analytics-venue-yet-to-arrive'), value: yetToArrive, color: AWAITING_COLOR }
					: null,
				arrivedOver > 0
					? { label: t('analytics-venue-over-arrived'), value: arrivedOver, color: OVER_ARRIVED_COLOR }
					: null,
				overCapacity > 0
					? { label: t('analytics-venue-over-capacity'), value: overCapacity, color: OVER_AWAITING_COLOR }
					: null,
				emptySeats > 0
					? { label: t('analytics-venue-empty-seats'), value: emptySeats, color: common.colors.grey19 }
					: null,
			].filter(Boolean) as DonutSlice[],
		[arrived, yetToArrive, arrivedOver, overCapacity, emptySeats, t]
	);

	// Single-series arrivals bar — memoised for the same React.memo'd-chart reason.
	const arrivalsBars = useMemo(
		() => [{ dataKey: 'count', color: CHART_SERIES_COLORS[0], name: t('analytics-venue-arrivals-series') }],
		[t]
	);

	// The donut is meaningful whenever the show has a hall or any issued tickets — including pre-show,
	// where it reads "0 arrived · N yet to arrive · M empty". The arrivals bar appears once scans begin.
	const hasAttendance = capacity > 0 || capacityTotals.issued > 0;
	const hasActivity = hasAttendance || totalArrivals > 0;

	// Tooltip + center use TOTALS across both bands. `arrivedTotal` is the door count clamped to issued
	// (a re-scan race can report more scans than issued — attendees can't exceed tickets out).
	const arrivedTotal = arrived + arrivedOver; // = min(scanned, issued)
	const awaited = yetToArrive + overCapacity; // all ticket holders not in yet
	const beyondHall = arrivedOver + overCapacity; // total issued beyond hall capacity
	const attendanceTip =
		beyondHall > 0
			? t(
					'analytics-venue-attendance-tip-over',
					'{{arrived}} arrived · {{yetToArrive}} yet to arrive · {{overCapacity}} over a {{capacity}}-seat hall',
					{ arrived: arrivedTotal, yetToArrive: awaited, overCapacity: beyondHall, capacity }
			  )
			: t(
					'analytics-venue-attendance-tip',
					'{{arrived}} arrived · {{yetToArrive}} yet to arrive · {{emptySeats}} empty (hall {{capacity}})',
					{ arrived: arrivedTotal, yetToArrive: awaited, emptySeats, capacity }
			  );

	return (
		<PunchupAnalyticsSection
			title={t('analytics-venue-operations')}
			description={t('analytics-venue-operations-desc')}
			isLoading={isLoading}
		>
			{!hasActivity ? (
				<PunchupAnalyticsEmptyState>{t('analytics-venue-empty')}</PunchupAnalyticsEmptyState>
			) : (
				<>
					<div className={styles.showDayRow}>
						{hasAttendance && (
							<div className={styles.attendanceCol}>
								<PunchupDonutChart
									title={t('analytics-venue-attendance')}
									centerLabel={`${arrivedTotal}`}
									centerTooltip={attendanceTip}
									/* `hasAttendance` (capacity > 0 || issued > 0) guarantees at least one non-zero slice
								   (an empty, yet-to-arrive, arrived, or over-capacity seat), so attendanceData is never empty here. */
									data={attendanceData}
									size={DONUT_SIZE}
									showLegend
									helpButton={
										<PunchupHelpButton
											mainText="Show Day attendance against the hall: who has come through the door, who still holds a ticket, and the seats left open. The centre is the door count."
											codeText={
												'arrived = tickets scanned at the door\nyet to arrive = ticket holders not in yet\nempty seats = hall capacity − issued\nover capacity = issued − hall (legacy oversold); its arrivals show in the deeper red'
											}
											secondaryText="The ring is your hall capacity — the same denominator as the Fill Rate tile. Arrived ÷ issued is the Show-up Rate. The red band means more tickets are out than the hall can seat; arrivals fill the hall first, then spill into it."
										/>
									}
								/>
							</div>
						)}
						{totalArrivals > 0 && (
							<div className={styles.arrivalsCol}>
								<PunchupStackedBarChart
									data={arrivals as unknown as Record<string, unknown>[]}
									bars={arrivalsBars}
									xAxisKey="label"
									title={t('analytics-venue-arrivals')}
									totalValue={totalArrivals}
									showLegend={false}
									yAxisFormatter={formatCompactNumber}
									xAxisFormatter={(v) => String(v)}
									helpButton={
										<PunchupHelpButton
											mainText="When attendees arrived, counted in equal time intervals across the scanning window — the shape of your door flow. Taller bars mark the busiest periods."
											secondaryText="Built from individual ticket scans. The full per-ticket scan log — including which gate — is on the Scanned Tickets page."
										/>
									}
								/>
							</div>
						)}
					</div>
					<PunchupAttendanceByType ticketTypeStats={scanHistory?.ticketTypeStats} capacityTypes={capacityTypes} />
				</>
			)}
		</PunchupAnalyticsSection>
	);
};

export default PunchupVenueOperationsSection;
